<template>
  <div class="dq-mini">
    <span class="text-muted small">{{ label }}</span>

    <div class="dropdown dq-mini-dd">
      <button
        :id="btnId"
        class="btn btn-outline-secondary btn-sm w-100 d-flex justify-content-between align-items-center dq-dd-btn"
        type="button"
        data-bs-toggle="dropdown"
        :disabled="disabled"
      >
        <span class="text-truncate">{{ selectedLabel }}</span>
        <i class="ti ti-chevron-down ms-2"></i>
      </button>

      <div class="dropdown-menu p-2 shadow dq-dd-menu" @click.stop>
        <button
          v-for="opt in normalizedOptions"
          :key="String(opt.value)"
          type="button"
          class="dropdown-item dq-dd-item"
          @click="select(opt.value)"
        >
          <div class="fw-semibold text-truncate">{{ opt.label }}</div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

defineOptions({ name: "P9Select" });

const props = defineProps({
  modelValue: { default: null }, // string | null
  label: { type: String, default: "P9 classification" },
  disabled: { type: Boolean, default: false },

  /**
   * Optional override:
   * [{ label: 'Normal benefit (default)', value: null }, ...]
   */
  options: {
    type: Array,
    default: () => [
      { label: "Normal benefit (default)", value: null },
      { label: "Non-cash benefit", value: "benefit_noncash" },
      { label: "Accommodation / quarters", value: "quarters" },
    ],
  },

  /**
   * Optional: unique id suffix for accessibility / to avoid duplicates in v-for
   */
  id: { type: [String, Number], default: "" },
});

const emit = defineEmits(["update:modelValue", "change"]);

const btnId = computed(() =>
  props.id ? `p9-select-btn-${props.id}` : `p9-select-btn`
);

const normalizedOptions = computed(() =>
  (props.options || []).map((o) => ({
    label: o?.label ?? String(o?.value ?? ""),
    value: o?.value ?? null,
  }))
);

const selectedLabel = computed(() => {
  const found = normalizedOptions.value.find((o) => o.value === props.modelValue);
  return found?.label || normalizedOptions.value[0]?.label || "Select";
});

function closeBootstrapDropdown() {
  // Simple & reliable: triggers Bootstrap's "outside click" close
  document.body.click();
}

function select(val) {
  emit("update:modelValue", val);
  emit("change", val);
  closeBootstrapDropdown();
}
</script>

<style scoped>
/* compact row */
.dq-mini {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px dashed rgba(15, 23, 42, 0.14);
  border-radius: 14px;
  margin-bottom: 10px;
}

/* dropdown sizing */
.dq-mini-dd {
  min-width: 220px;
  width: 220px;
  flex: 0 0 auto;
}

.dq-dd-btn {
  border-radius: 12px;
  border-color: rgba(15, 23, 42, 0.12);
  background: #fff;
}

.dq-dd-menu {
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  z-index: 2000; /* modal-safe */
  width: 100%;
}

.dq-dd-item {
  border-radius: 10px;
  padding: 10px 10px;
}

.dq-dd-item:active {
  background: rgba(13, 110, 253, 0.08);
}
</style>
